/** @prettier */

import {memo, useEffect, useState} from 'react';
import {
  Card,
  CardContent,
  Grid,
  Paper,
  CardHeader,
  Box,
  Stack,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import GraphHeader from './GraphAppBar';
import BarChartGraph from '../../StackBarChart';
import LinearWithValueLabel from '../../LinearProgressWithLabel';
import {StaticDatePicker, StaticComparisonDatePicker} from './DatePicker';
import TextFieldSelectBox from '../../SelectBox';
import {PowerTrendSelectButton} from './molculate/PowerTrendButton';
import {AddButton} from '../../BasicButton';
//type import
import {GraphDataRange, graphProps} from '../../../Type/TypeDef';
//Style import
import '../../../css/common/common.css';

import {useInteractJS} from '../../../functions/positionMove';
import {makeGraphDataSite} from '../../../functions/makeSiteGraphData';
import {useGetStartEndDate} from '../../../hooks/graph/useGetStartEndDate';
import {useSiteChangeStartEndDate} from '../../../hooks/graph/useSiteChangeStartEndDate';
import {pullDownMenu} from '../../../define';
import {makeXLabel} from '../../../functions/graphFunction';

/**
 * サイトのグラフ
 */
const SiteGraph = memo((props: {graphProps: graphProps}) => {
  const trans = useTranslation().t; //翻訳関数
  //prettier-ignore
  const {siteID, graphTitle, graphTarget, setGraphParam} = props.graphProps;

  //prettier-ignore
  //グラフの表示モード（空調機運転モード毎(冷暖他)/熱源機運転モード毎(冷暖) or 空調機/熱源機）
  const [graphMode, setGraphMode] = useState<'Individual' | 'Aggregation'>('Individual');
  //日付の表示（1日/1週間/1か月/1年/10年）
  const [dateRange, setDateRange] = useState<GraphDataRange>('day');
  //prettier-ignore
  const [barGraphDataCool, setBarGraphDataCool] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ冷房
  //prettier-ignore
  const [comparisonBarGraphDataCool, setComparisonBarGraphDataCool] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ冷房（比較日）
  //prettier-ignore
  const [barGraphDataHot, setBarGraphDataHot] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ暖房
  //prettier-ignore
  const [comparisonBarGraphDataHot, setComparisonBarGraphDataHot] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ暖房（比較日）
  //prettier-ignore
  const [barGraphDataOther, setBarGraphDataOther] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータその他
  //prettier-ignore
  const [comparisonBarGraphDataOther, setComparisonBarGraphDataOther] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータその他（比較日）
  //prettier-ignore
  const [barGraphDataRTCool, setBarGraphDataRTCool] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータリモ端冷房
  //prettier-ignore
  const [comparisonBarGraphDataRTCool, setComparisonBarGraphDataRTCool] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータリモ端冷房（比較日）
  //prettier-ignore
  const [barGraphDataRTHot, setBarGraphDataRTHot] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータリモ端暖房
  //prettier-ignore
  const [comparisonBarGraphDataRTHot, setComparisonBarGraphDataRTHot] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータリモ端暖房（比較日）
  //prettier-ignore
  const [barGraphDataAirConditioner ,setBarGraphDataAirConditioner] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ空調機
  //prettier-ignore
  const [comparisonBarGraphDataAirConditioner ,setComparisonBarGraphDataAirConditioner] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ空調機（比較日）
  //prettier-ignore
  const [barGraphDataOtherEquipment ,setBarGraphDataOtherEquipment] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータRT機器
  //prettier-ignore
  const [comparisonBarGraphDataOtherEquipment ,setComparisonBarGraphDataOtherEquipment] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータRT機器（比較日）
  const [xDate, setXDate] = useState<string[] | []>([]); //通常日の日付データ
  const [comparisonXDate, setComparisonXDate] = useState<string[]>([]); //比較日の日付データ
  const [xAxisLabel, setXAxisLabel] = useState<string[]>([]); //x軸に表示するラベル
  const [activeComparison, setActiveComparison] = useState(false); //比較データ表示判定

  const interact = useInteractJS(); //グラフダイアログの移動に必要な関数

  //prettier-ignore
  //グラフに表示する日付の範囲を取得
  const {timeZone, graphStartDate, graphEndDate, comparisonGraphStartDate, comparisonGraphEndDate, setGraphDispDate, setComparisonGraphDispDate} = useGetStartEndDate(siteID)

  //日付や対象が変更されたとき、グラフに描画するデータを再取得
  const {
    oneSiteInfo: siteInfo,
    fileNameDate: csvFileNameDate,
    graphDispError,
    apiWait,
  } = useSiteChangeStartEndDate(
    siteID,
    graphStartDate,
    graphEndDate,
    timeZone,
    dateRange
  );
  const {
    oneSiteInfo: comparisonSiteInfo,
    fileNameDate: comparisonCsvFileNameDate,
    graphDispError: comparisonGraphDispError,
    apiWait: comparisonApiWait,
  } = useSiteChangeStartEndDate(
    siteID,
    comparisonGraphStartDate,
    comparisonGraphEndDate,
    timeZone,
    dateRange
  );

  //受信APIデータ変更時、グラフ表示項目変更時にグラフ描画用データを作成する
  useEffect(() => {
    if (siteInfo === undefined) return;
    const ret = makeGraphDataSite(
      dateRange,
      siteInfo,
      timeZone,
      graphMode,
      graphStartDate,
      trans
    );

    setXDate(ret.date);
    setBarGraphDataCool(ret.powerCool);
    setBarGraphDataHot(ret.powerHot);
    setBarGraphDataOther(ret.powerOther);
    setBarGraphDataRTCool(ret.powerRTCool);
    setBarGraphDataRTHot(ret.powerRTHot);
    setBarGraphDataAirConditioner(ret.powerAirConditioner);
    setBarGraphDataOtherEquipment(ret.powerOtherEquipment);
  }, [trans, siteInfo, graphMode]);

  //比較ボタン押下時に、別の日のグラフ描画用データを作成する
  useEffect(() => {
    if (comparisonSiteInfo === undefined) return;
    const comparisonRet = makeGraphDataSite(
      dateRange,
      comparisonSiteInfo,
      timeZone,
      graphMode,
      comparisonGraphStartDate,
      trans
    );

    setComparisonXDate(comparisonRet.date);
    setComparisonBarGraphDataCool(comparisonRet.powerCool);
    setComparisonBarGraphDataHot(comparisonRet.powerHot);
    setComparisonBarGraphDataOther(comparisonRet.powerOther);
    setComparisonBarGraphDataRTCool(comparisonRet.powerRTCool);
    setComparisonBarGraphDataRTHot(comparisonRet.powerRTHot);
    setComparisonBarGraphDataAirConditioner(comparisonRet.powerAirConditioner);
    setComparisonBarGraphDataOtherEquipment(comparisonRet.powerOtherEquipment);
  }, [trans, comparisonSiteInfo, graphMode]);

  //x軸に表示するラベルを作成する
  useEffect(() => {
    makeXLabel({
      xDate: xDate,
      comparisonXDate: comparisonXDate,
      dateRange: dateRange,
      graphType: 'bar',
      activeComparison: activeComparison,
      setXAxisLabel: setXAxisLabel,
    });
  }, [xDate, comparisonXDate, activeComparison]);

  //データ取得範囲で10年が選択された際に、比較機能をOFFにする
  useEffect(() => {
    if (dateRange === 'tenYear') {
      setActiveComparison(false);
    }
  }, [dateRange]);

  /** 比較機能のON/OFFを切り替える関数 */
  const handleOnComparison = () => {
    setActiveComparison(!activeComparison);
  };

  /**
   * グラフ
   * @returns JSX.Element
   */
  const GraphDisplay = () => {
    //比較表示
    if (activeComparison) {
      switch (graphMode) {
        //Each operation modeの場合
        case 'Individual':
          return (
            <BarChartGraph
              graphMode={graphMode}
              graphTarget={graphTarget}
              xLabel={xAxisLabel}
              dateRange={dateRange}
              csvFileNameDate={`${csvFileNameDate}, ${comparisonCsvFileNameDate}`}
              csvFileNameTarget={graphTitle}
              barGraphData1={barGraphDataCool}
              barGraphData2={barGraphDataHot}
              barGraphData3={barGraphDataOther}
              barGraphData4={barGraphDataRTCool}
              barGraphData5={barGraphDataRTHot}
              comparisonBarGraphData1={comparisonBarGraphDataCool}
              comparisonBarGraphData2={comparisonBarGraphDataHot}
              comparisonBarGraphData3={comparisonBarGraphDataOther}
              comparisonBarGraphData4={comparisonBarGraphDataRTCool}
              comparisonBarGraphData5={comparisonBarGraphDataRTHot}
            />
          );
        //AC/Otherの場合
        case 'Aggregation':
          return (
            <BarChartGraph
              graphMode={graphMode}
              graphTarget={graphTarget}
              xLabel={xAxisLabel}
              dateRange={dateRange}
              csvFileNameDate={csvFileNameDate}
              csvFileNameTarget={graphTitle}
              barGraphData1={barGraphDataAirConditioner}
              barGraphData4={barGraphDataOtherEquipment}
              comparisonBarGraphData1={comparisonBarGraphDataAirConditioner}
              comparisonBarGraphData4={comparisonBarGraphDataOtherEquipment}
            />
          );
      }
    }
    //通常表示
    switch (graphMode) {
      //Each operation modeの場合
      case 'Individual':
        return (
          <BarChartGraph
            graphMode={graphMode}
            graphTarget={graphTarget}
            xLabel={xAxisLabel}
            dateRange={dateRange}
            csvFileNameDate={csvFileNameDate}
            csvFileNameTarget={graphTitle}
            barGraphData1={barGraphDataCool}
            barGraphData2={barGraphDataHot}
            barGraphData3={barGraphDataOther}
            barGraphData4={barGraphDataRTCool}
            barGraphData5={barGraphDataRTHot}
          />
        );
      //AC/Otherの場合
      case 'Aggregation':
        return (
          <BarChartGraph
            graphMode={graphMode}
            graphTarget={graphTarget}
            xLabel={xAxisLabel}
            dateRange={dateRange}
            csvFileNameDate={csvFileNameDate}
            csvFileNameTarget={graphTitle}
            barGraphData1={barGraphDataAirConditioner}
            barGraphData4={barGraphDataOtherEquipment}
          />
        );
    }
  };

  return (
    <Paper
      elevation={10}
      className="graph"
      ref={interact.ref}
      style={{...interact.style}}>
      <Card className="graph__card">
        <CardHeader
          disableTypography
          title={
            <GraphHeader
              setGraphParam={setGraphParam}
              graphTitle={graphTitle}
            />
          }
          className="graph__card__header"
        />
        <CardContent className="graph__card__main">
          {/* 消費電力とトレンドグラフの選択 */}
          <PowerTrendSelectButton graphType="bar" disableTrend={true} />
          {/* グラフデータ設定欄 */}
          {timeZone !== '' && (
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              className="graph__card__main__menu">
              <Grid item xs="auto" container alignItems="center" spacing={2}>
                {/* カレンダー */}
                <Grid item xs="auto">
                  <StaticDatePicker
                    setGraphDisplayDate={setGraphDispDate}
                    timeZone={timeZone}
                    dateRange={dateRange}
                  />
                </Grid>
                {/* 比較用カレンダー */}
                <Grid item xs>
                  {activeComparison ? (
                    <StaticComparisonDatePicker
                      setGraphDisplayDate={setComparisonGraphDispDate}
                      timeZone={timeZone}
                      dateRange={dateRange}
                      onCloseClick={handleOnComparison}
                    />
                  ) : (
                    dateRange !== 'tenYear' && (
                      //比較用カレンダー表示ボタン
                      <AddButton
                        onClick={handleOnComparison}
                        buttonText={trans('SiteGraph.buttonTxt.comparison')}
                      />
                    )
                  )}
                </Grid>
              </Grid>
              <Grid item xs="auto" container spacing={3}>
                {/* グラフの種類設定セレクトボックス */}
                <Grid item xs="auto">
                  <TextFieldSelectBox
                    label={trans('SiteGraph.txtField.label.graphMode')}
                    choices={pullDownMenu.SITE_GRAPH_MODE}
                    value={graphMode}
                    setValue={setGraphMode}
                    id={'graphModeSelect'}
                    class="graph__card__main__menu__selectBox--graphMode"
                  />
                </Grid>
                {/* グラフ表示期間設定セレクトボックス */}
                <Grid item xs="auto">
                  <TextFieldSelectBox
                    label={trans('SiteGraph.txtField.label.graphRange')}
                    choices={pullDownMenu.GRAPH_RANGE}
                    value={dateRange}
                    setValue={setDateRange}
                    id={'graphRangeSelect'}
                    class="graph__card__main__menu__selectBox"
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {/* 消費電力グラフ */}
          {apiWait || comparisonApiWait ? (
            //ロード中表示
            <Stack alignItems="center" justifyContent="center">
              {graphDispError || comparisonGraphDispError ? (
                <h1 className="graph__card__main__errorMessage">
                  {trans('SiteGraph.errMsg.gettingDataFailure')}
                </h1>
              ) : (
                <Box className="graph__card__main__progress">
                  <LinearWithValueLabel />
                </Box>
              )}
            </Stack>
          ) : graphDispError || comparisonGraphDispError ? (
            <Stack justifyContent="center" alignItems="center">
              <h1 className="graph__card__main__errorMessage">
                {trans('SiteGraph.errMsg.gettingDataFailure')}
              </h1>
            </Stack>
          ) : (
            <GraphDisplay />
          )}
        </CardContent>
      </Card>
    </Paper>
  );
});
export default SiteGraph;
